import React, {useEffect, useMemo, useState} from 'react'
import {
	Backdrop,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Fade,
	Grid,
	IconButton,
	Modal, Skeleton,
	Typography,
	useTheme
} from '@mui/material'
import {
	motion
}                                            from 'framer-motion'
import {
	doGet
}                                            from "../../util/do-fetch"
import {
	createCheckoutSession
}                                            from "../../util/mini"
import {
	DEBUG,
	logger
}                                            from "../../util/log-utils"
import {
	fmt2Dec
}                                            from "../../util/string"
import {
	Check
}                                            from "@mui/icons-material"
import CloseIcon                             from "@mui/icons-material/Close"
import {
	useNavigate
}                                            from "react-router-dom"
import useQuery                              from "../../hooks/useQuery"
import CountdownTimer                        from "../CountdownTimer"
import LeftRight                             from "../LeftRight"

const COUPON_CODE = {code: "FAST15", percent: 15}
const COUPON_ENCODED = btoa(JSON.stringify(COUPON_CODE))

const log = logger("ExtendOverlay", DEBUG)

/*
 * Plan bullets and badges, BY POSITION (200).
 *
 * Position is safe here and duration is not. Options arrive ordered by sort_key (fetchPackage), and
 * across the 122 packages that carry options the order is always shortest -> middle -> longest, so
 * index 0/1/2 reliably means "quick revision / steady prep / most time".
 *
 * Nothing here NAMES a duration, deliberately. No package sells 1/3/6 months: the two real line-ups
 * are 1 Week / 1 Month / 3 Months and 1 Week / 3 Months / 6 Months, and every package leads with a
 * week. Copy that said "1 Month" would be wrong on every kit in the catalogue, so the plan's own
 * name is interpolated through [name] instead.
 */
const highlights = [
	[
		"Great for last-minute review",
		"Unlimited access for [name]",
		"Pay once. No recurring charges."
	],
	[
		"Ideal for steady, consistent prep",
		"Unlimited access for [name]",
		"Pay once. No recurring charges."
	],
	[
		"More time and flexibility",
		"Unlimited access for [name]",
		"Pay once. No recurring charges."
	],
]

// middle plan is the one most people take; the longest is the cheapest per day
const badges = [null, 'Most Popular', '⭐️ Best Value']

const ExtendOverlay = ({
	                       variant="expired", // or buy
	                       open,
	                       prepKit,
	                       coupon,
	                       onCancelBuy
                       }) => {

	const theme = useTheme()
	const navigate = useNavigate()
	const {wait} = useQuery()
	const [selectedPackage, setSelectedPackage] = useState(null)
	const [loading, setLoading] = useState(true)
	const [countdownComplete, setCountdownComplete] = useState(false)
	// 'published' | 'archived' | 'none' -- see /mini/prepkit/:id/renewal-options (194)
	const [packageSource, setPackageSource] = useState(null)

	const showDiscount = useMemo(() => {
		return wait !== undefined
	}, [wait])

	const isBuy = useMemo(()=> {
		return variant === "buy"
	}, [variant])

	useEffect(() => {
		if (!prepKit || !prepKit.license) {
			setSelectedPackage(null)
			/*
			 * There is nothing to look up, so this is FINISHED, not loading (193). Leaving `loading`
			 * set here was one of the ways the dialog ended up animating a skeleton forever.
			 */
			setLoading(false)
		}
		else {
			if (prepKit.license.package_id !== selectedPackage?.id) {
				/*
				 * The RENEWAL route, not /mini/shop (194).
				 *
				 * The shop route is published-only -- correctly, it is public -- so a kit whose
				 * package has since been archived got an empty answer and this dialog had nothing to
				 * offer. Someone who already paid could not pay again.
				 *
				 * /mini/prepkit/:id/renewal-options is authenticated and scoped to a kit the caller
				 * owns, so it can fall back to the archived package they actually hold. Stripe needed
				 * no change: checkout and fulfilment both resolve the package without a published
				 * filter, and package_option rows outlive their package being archived.
				 */
				log.debug(`fetching renewal options for prepKit [${prepKit.id}]`)
				const controller = new AbortController()

				const fn = async () => {
					setLoading(true)
					try {
						const response = await doGet(
							`/mini/prepkit/${prepKit.id}/renewal-options`,
							{signal: controller.signal}
						)

						if (!response.ok) {
							log.debug(`renewal options lookup failed with status ${response.status}`)
							return
						}

						const body = await response.text()
						if (!body) {
							log.debug('empty renewal options response')
							return
						}

						/*
						 * `package` is null with source 'none' when there is genuinely nothing to sell --
						 * the option rows or the package row were deleted rather than archived. The
						 * NoOptions state below covers it.
						 */
						const data = JSON.parse(body)
						setPackageSource(data.source || null)
						setSelectedPackage(data.package || null)
					}
					catch (err) {
						if (err.name !== 'AbortError') {
							log.error(err)
						}
					}
					finally {
						setLoading(false)
					}
				}

				fn()
				return () => controller.abort()
			}
		}
	}, [prepKit, prepKit?.license])

	const handleOptionClick = async (option) => {
		let cancelUrl = window.location.href

		const url = await createCheckoutSession(
			selectedPackage,
			option,
			prepKit && prepKit.id,
			coupon && coupon.code,
			cancelUrl
		)
		if (url) window.location.href = url
	}

	let title
	let subtitle

	if (showDiscount) {
		title = 'Before you go — save 15%'
		subtitle = <span>
			Choose a plan in the next
			{
				countdownComplete
					? <span> two minutes </span>
					: <CountdownTimer
						color={theme.palette.error.main}
						minutes={1}
						seconds={59}
						onComplete={() => setCountdownComplete(true)}
					/>
			}
			and save {coupon.percent}% on your Birdsy access.
		</span>
	}
	else {
		/*
		 * ONE headline for every situation (200): first purchase, trial running out, expired kit.
		 *
		 * It used to fork three ways on isBuy / isExpiredTrial, which produced "Extend your free
		 * trial" for a buy and "Your access has expired" for a renewal — three voices for one
		 * decision, and two of them led with the bad news. The progress reassurance is in the
		 * subheadline now, so it is said once rather than only on the branch that remembered to.
		 */
		title = 'Choose your Birdsy access'
		subtitle = 'Choose the plan that fits your timeline. Already started? Your progress is saved, so you can pick up where you left off.'
	}

	return <>
		<Modal
			open={open}
			closeAfterTransition
			disableAutoFocus={true}
			slots={{backdrop: Backdrop}}
			slotProps={{
				backdrop: {
					timeout: 300,
					sx: {
						backgroundColor: 'rgba(0,0,0,0.5)',
						backdropFilter: 'blur(6px)'
					}
				}
			}}
		>
			<Fade in={open}>
				<motion.div
					layout
					transition={{duration: 0.28, ease: "easeOut"}}
				>
					<Box
						sx={{
							position: 'absolute',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							width: '90%',
							maxWidth: 1024,
							bgcolor: 'background.paper',
							p: {xs: 3, sm: 5},
							textAlign: 'center',
							maxHeight: '90vh',
							overflowY: 'auto'
						}}
					>
						<IconButton
							size="large"
							sx={{position: "absolute", top: "10px", right: "10px"}}
							onClick={() => {
								if(isBuy) {
									onCancelBuy && onCancelBuy()
								}
								else {
									wait
										? navigate('/prepkit')
										: navigate(`?coupon=${COUPON_ENCODED}&wait=true`)
								}
							}}
						>
							<CloseIcon/>
						</IconButton>
						<Box mb={4}>
							<Typography variant="h4" fontWeight="bold" letterSpacing="-0.05em" gutterBottom>
								{
									wait
										? <motion.div
											initial={{color: theme.palette.error.main}}
											animate={{color: theme.palette.text.primary}}
											transition={{
												duration: 0.4,
												ease: 'easeOut',
												repeat: 2,
												repeatType: 'reverse'
											}}
										>
											{title}
										</motion.div>
										: <span>{title}</span>
								}
							</Typography>
							<Typography variant="h6">
								{subtitle}
							</Typography>
						</Box>

						<Grid container spacing={{xs: 2, sm: 1, md: 0, lg: 0}} justifyContent="center"
						      textAlign={"left"}>
							{
								/*
								 * Three states, not two (193). `loading` used to be ignored here, so the
								 * skeleton was the fallback for EVERY outcome -- an archived or
								 * unpublished package, a failed lookup, a package with no options -- and
								 * the dialog animated placeholders indefinitely with nothing to click and
								 * nothing said. An expired customer could not renew and was not told why.
								 */
								loading
									? <SkeletonCards/>
									: selectedPackage?.options
										? <ProductOptionCards selectedPackage={selectedPackage}
										                      coupon={coupon}
										                      onOptionClick={handleOptionClick}/>
										: <NoOptions onBrowse={() => navigate('/prep')}/>
							}
						</Grid>

						<Box>
							{/*
							  * Said plainly when the plan shown is no longer on sale (194, 195). They are
							  * being offered the package they originally bought -- archived, or rebuilt from
							  * their own licence -- which is the right thing to offer and not something to
							  * present as a current listing.
							  */}
							{(packageSource === 'archived' || packageSource === 'restored') && selectedPackage?.options && (
								<Box mt={3}>
									<Typography variant="body2" color="text.secondary" textAlign="center">
										This is the plan you originally purchased. It is no longer sold to new
										customers, but you can renew it.
									</Typography>
								</Box>
							)}
							{/*
							  * Under all three plans, not under each button (200). The per-card line said
							  * the same thing three times, which is three chances to read it as a
							  * per-plan condition rather than how Birdsy always works.
							  */}
							<Box mt={4}>
								<Typography variant="body2" color="text.secondary">
									Instant access • One-time purchase • No recurring charges
								</Typography>
								<Typography variant="body2" color="text.primary" sx={{mt: 0.5}}>
									✅ 30-day money-back guarantee. Full refund if you’re not satisfied.
								</Typography>
							</Box>
							<Typography variant={"body1"}
							            sx={{
								            p: 4,
								            fontSize: "16px",
								            fontStyle: "italics",
								            border: `1px solid ${theme.palette.divider}`,
								            backgroundColor: theme.palette.background.default,
								            mt: 2,
								            mx: 4,
								            borderRadius: 3,
								            textAlign: 'center'
							            }}
							>
								<i>“I was worried about remembering all the details, but Birdsy kept reinforcing
									what mattered — and it stuck.”</i>
								<br/>- Jorge, Nashville TN ⭐️⭐️⭐️⭐️⭐️
							</Typography>
						</Box>
					</Box>
				</motion.div>
			</Fade>
		</Modal>
	</>
}

const ProductOptionCards = ({
	selectedPackage, coupon, onOptionClick
}) => {

	const theme = useTheme()

	const discountAmount = (price) => {
		// apply discount
		let discounted = price
		if (coupon) {
			discounted = price - (price * (coupon.percent / 100))
		}

		return fmt2Dec(discounted)
	}

	return selectedPackage?.options?.map((option, idx) => {
			const amt = option.price_in_cents / 100
			const discAmt = discountAmount(amt)

			return <Grid item xs={12} sm={6} md={4} key={idx}>
				<Card
					sx={{
						cursor: 'pointer',
						borderRadius: 3,
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						pt: 2,
						px: 1,
						transition: 'all 0.3s ease',
						...(idx === 1
							? {
								transform: 'scale(1.05, 1.1)',
								boxShadow: 8,
								border: '2px solid',
								borderColor: 'primary.light',
								zIndex: 10,
								'&:hover': {
									boxShadow: 10,
								},
							}
							: {
								boxShadow: 3,
								border: '1px solid',
								borderColor: 'divider',
								zIndex: 0,
								'&:hover': {
									boxShadow: 6,
								},
							}),
					}}
					onClick={() => onOptionClick(option)}
				>
					<CardContent>
						<LeftRight
							left={
								<Typography
									variant="body1"
									fontWeight="bold"
									color="primary"
								>
									{option.name}
								</Typography>
							}
							right={
								badges[idx] &&
								<Chip
									size={"small"}
									variant={idx === 1 ? "contained" : "outlined"}
									color={"primary"}
									label={badges[idx]}
									sx={{
										fontSize: "10px",
									}}
								/>
							}
						/>
						<Box display="flex" flexDirection="row" alignItems="flex-end"
						     justifyContent="flex-start" mb={1}>
							<Typography
								variant="h3"
								fontWeight="bold"
								fontSize="36px"
								letterSpacing="-1.2px"
							>
								${fmt2Dec(discAmt)}
							</Typography>
							<Typography
								variant="body2"
								ml={1}
								mb={0.5}
							>
								one-time
							</Typography>
						</Box>
						{
							discAmt < amt &&
							<Box>
								<Typography variant="body1">
										<span style={{
											color: theme.palette.error.main,
											textDecoration: 'line-through'
										}}>
											${fmt2Dec(amt)}
										</span>
									{/*
									  * The amount stays COMPUTED from this plan's own price (200).
									  *
									  * The brief gave fixed figures — save $3.00 / $4.50 / $6.00 — which
									  * match list prices of $20 / $30 / $40. The catalogue does not sell
									  * those: the middle plan is $9.99 or $29.99 and the longest is $19.99
									  * or $39.99, so a hardcoded "$3.00" would be wrong on the majority of
									  * kits and would be a false price, not just off copy. Only the wording
									  * changed.
									  */}
									<span>
											&nbsp;Save ${fmt2Dec(amt - discAmt)} today
										</span>
								</Typography>
							</Box>
						}

						<Box sx={{my: 3}}>
							{
								highlights[idx].map((hl, idx) => (
									<Box display="flex"
									     flexDirection="row"
									     justifyContent="flex-start"
									     alignItems="center"
									     sx={{
										     width: "100%",
										     mb: 1,
									     }}
									     key={idx}
									>
										<Check sx={{
											color: theme.palette.success.main,
											mr: 1,
											fontSize: 18,
											width: 18,
											height: 18
										}}/>
										<Typography variant="body2" color="text.secondary" ml={1}>
											{hl.replace('[name]', option.name)}
										</Typography>
									</Box>
								))
							}
						</Box>
						<Button
							variant={idx === 1 ? "contained" : "outlined"}
							color={"primary"}
							fullWidth
							sx={{
								textTransform: 'none',
								fontWeight: 600,
								mt: 0,
								boxShadow: 'none',
								'&:hover': {
									boxShadow: 'none',
								},
								opacity: idx === 1 ? 1 : 0.9,
							}}
						>
							Choose This Plan
						</Button>
					</CardContent>
				</Card>
			</Grid>
		}
	)
}

/*
 * Shown when the lookup has finished and there is no plan to offer -- most often a kit bought
 * against a package that has since been archived or unpublished, which /mini/shop answers with an
 * empty 200.
 *
 * It offers the catalogue rather than a dead end: the customer's package may be gone, but the
 * category almost certainly still sells something, and they arrived here wanting to pay.
 */
const NoOptions = ({onBrowse}) => (
	<Grid item xs={12}>
		<Box textAlign="center" py={2}>
			<Typography variant="body1" color="text.primary" gutterBottom>
				Renewal options aren't available for this PrepKit right now.
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
				Your progress is saved. Browse the catalogue to pick up where you left off.
			</Typography>
			<Button variant="contained" onClick={onBrowse}>
				Browse plans
			</Button>
		</Box>
	</Grid>
)

const SkeletonCards = () => {
	return [1, 2, 3].map(i => (
		<Grid item xs={12} sm={6} md={4} key={i}>
			<Card
				sx={[
					{
						borderRadius: 3,
						border: '1px solid',
						borderColor: 'divider',
						p: 1,
						height: '100%'
					},
					i===2 &&
					{
						transform: 'scale(1.05, 1.13)',
					}
				]}
			>
				<CardContent>
					<Skeleton variant="text" width={120} height={22}/>

					<Box mt={2} mb={1}>
						<Skeleton variant="text" width={140} height={48}/>
					</Box>

					<Box sx={{my: 3}}>
						<Skeleton variant="text" width={'90%'} height={18}/>
						<Skeleton variant="text" width={'85%'} height={18}/>
						<Skeleton variant="text" width={'80%'} height={18}/>
					</Box>

					<Skeleton variant="rectangular" height={42} sx={{borderRadius: 1}}/>
				</CardContent>
			</Card>
		</Grid>
	))
}

export default ExtendOverlay
